import React from "react";
import { Flex } from "../../../Flex";
import { Span } from "../../../Span";
import { List } from "../../../List";
import { ListItem } from "../../../ListItem";
import { IconPopupTip } from "../../../IconPopupTip";
import { DisconnectionContactDetailsRole } from "../DisconnectionContactDetailsRole";
import { DisconnectionContactDetails } from "../../../../types/DisconnectionContactDetails";
import styled from "styled-components";
import { position, space, SpaceProps } from "styled-system";
import { Line } from "../../../Line";
import { DisconnectionContactDetailsRoleRadio } from "../DisconnectionContactDetailsRoleRadio";
import { Box } from "../../../Box";
import { useUserAuthentication } from "../../../../contexts/UserAuthenticationProvider/UserAuthenticationProvider";
import {
  mapPropertyOwnerHeader,
  mapRequestHeader,
  mapSiteContactHeader,
} from "../DisconnectionFormUtils/DisconnectionFormUtils";

interface Props {
  id: string;
  contactDetails: DisconnectionContactDetails;
}

export const ContactRoleHeader = styled.span<SpaceProps>`
  ${space}
  ${position}
  display: inline-block;
  font-size: ${({ theme }) => theme.fontSizes[4]};
  line-height: ${({ theme }) => theme.lineHeights[4]};
  font-weight: ${({ theme }) => theme.fontWeights.heavy};
`;

const sectionSeparator = (
  <Line
    my={5}
    width="100%"
    border="1px solid"
    borderColor="neutrals.ln30"
    bg="neutrals.ln30"
    opacity="1"
    height={0}
  />
);

const shouldDisablePropertyOwnersSiteContactOption = (
  contactDetails: DisconnectionContactDetails,
): boolean => {
  return "property-owner-contact" === contactDetails.siteContactSameAsRole;
};

const shouldDisableSiteContactsOwnersOption = (
  contactDetails: DisconnectionContactDetails,
): boolean => {
  return "site-access-contact" === contactDetails.propertyOwnerSameAsRole;
};

export const DisconnectionContactDetailsForm: React.FC<Props> = ({
  id,
  contactDetails,
}) => {
  const {
    authenticatedUserContext: { isLoggedIn },
  } = useUserAuthentication();

  return (
    <Flex flexDirection="column" id={id} mb={4}>
      <Flex mt={0} mb={4}>
        <Span fontSize={4} lineHeight={4}>
          Contact details
        </Span>
        <IconPopupTip icon="help" ml={["auto", "auto", "auto", 3]}>
          {
            "We require the contact details of all people / roles, as we may need to contact them at different times during the job. Sometimes one person fulfils several roles e.g. the requester and the property owner may be the same person."
          }
        </IconPopupTip>
      </Flex>
      <Span lineHeight={2} mt={0} mb={4}>
        We require contact information for:
        <List mb={0}>
          <ListItem>
            <Span fontWeight={"bold"}>the requester</Span>
            <Span>
              {" "}
              - the person who is authorised by the property owner to request
              the permanent disconnection
            </Span>
          </ListItem>
          <ListItem>
            <Span fontWeight={"bold"}>the property owner</Span>
            <Span>
              {" "}
              - the person who holds legal ownership of the property and is
              authorising the permanent disconnection
            </Span>
          </ListItem>
          <ListItem>
            <Span fontWeight={"bold"}>the site contact</Span>
            <Span>
              {" "}
              - the person designated as the primary contact for access or
              addressing any issues or concerns whilst our crew are on site
            </Span>
          </ListItem>
        </List>
      </Span>

      <ContactRoleHeader mb={4}>
        {mapRequestHeader(contactDetails)}
      </ContactRoleHeader>
      <DisconnectionContactDetailsRole
        id={`${id}-requester`}
        contactDetails={contactDetails.requester}
        idPrefix={"contactDetails.requester"}
        namePrefix={"contactDetails.requester"}
        shouldDisableContactDetails={isLoggedIn}
        emailPreferenceDisabled={true}
      />

      {sectionSeparator}

      <ContactRoleHeader mb={4}>
        {mapPropertyOwnerHeader(contactDetails)}
      </ContactRoleHeader>
      <DisconnectionContactDetailsRoleRadio
        id={`${id}-property-owner-roles`}
        namePrefix={"contactDetails.propertyOwnerSameAsRole"}
        currentValue={contactDetails.propertyOwnerSameAsRole}
        showPropertyOwnerContactOption={false}
        disableSiteContactOption={shouldDisablePropertyOwnersSiteContactOption(
          contactDetails,
        )}
      />

      {contactDetails.propertyOwnerSameAsRole === "new-contact" && (
        <Box mt={4}>
          <DisconnectionContactDetailsRole
            id={`${id}-property-owner`}
            contactDetails={contactDetails.propertyOwner}
            idPrefix={"contactDetails.propertyOwner"}
            namePrefix={"contactDetails.propertyOwner"}
            optionalMobile={true}
          />
        </Box>
      )}

      {sectionSeparator}

      <ContactRoleHeader mb={4}>
        {mapSiteContactHeader(contactDetails)}
      </ContactRoleHeader>
      <DisconnectionContactDetailsRoleRadio
        id={`${id}-site-contact-roles`}
        namePrefix={"contactDetails.siteContactSameAsRole"}
        currentValue={contactDetails.siteContactSameAsRole}
        showSiteContactOption={false}
        disablePropertyOwnerContactOption={shouldDisableSiteContactsOwnersOption(
          contactDetails,
        )}
      />
      {contactDetails.siteContactSameAsRole === "new-contact" && (
        <Box mt={4}>
          <DisconnectionContactDetailsRole
            id={`${id}-site-contact`}
            contactDetails={contactDetails.siteContact}
            idPrefix={"contactDetails.siteContact"}
            namePrefix={"contactDetails.siteContact"}
            optionalEmail={true}
          />
        </Box>
      )}
    </Flex>
  );
};
